import { ApolloProvider } from '@apollo/client';
import React, {useRef, useState, useCallback, useEffect} from 'react';
import {
    FETCH_ALL_CATEGORIES,
    FETCH_BLOGS,
    FETCH_FEATURED_BLOG,
} from '@services/blogs.service';
import FeaturedBlog from '@components/Blogs/FeaturedBlog';
import { MasonryGrid } from '@components/MasonryGrid/masonryGrid';
import { Breadcrumbs } from '@atoms/index';
import { blogBreadcrumbs, getBreadCrumbs } from '@components/Blogs/helper';
import client from '@lib/apollo-client';
import NextHeadTag from '@ui/molecules/NextHeadTag';
import { HeadTagData } from '@helpers/headTagData';
import { regexPattern } from '@helpers/regex';
import dynamic from 'next/dynamic';
import BlogFilters from '@components/Blogs/BlogFillters';
import useWindowDimensions from '@ui/hooks/useWindowDimensions';
import { debounce } from 'lodash';
import {logEvent, moengageEventConstants} from "@services/moengage";

const BlogListItem = dynamic(() => import('@components/Blogs/BlogListItem'));
const BlogSearchResult = dynamic(() => import('@components/Blogs/BlogSearchResult'));

const itemsPerPage = 12;
const dummyBlogs = Array.from({ length: 6 }, (_, i) => ({ id: i }));

const baseBlogFilters = {
    first: 12,
    after: null,
    category: '',
    searchTerm: '',
};

function Blogs({
    categories,
    featuredBlog,
    blogList: initialBlogList,
    blogListPageInfo: initialBlogListPageInfo,
}) {
    const filterSectionRef = useRef(null);
    const [lastSearchedTerm, setLastSearchedTerm] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [areBlogListLoading, setAreBlogListLoading] = useState(false);
    const [blogList, setBlogList] = useState(initialBlogList);
    const [blogListPageInfo, setBlogListPageInfo] = useState(initialBlogListPageInfo);
    const [cursors, setCursors] = useState({ 0: initialBlogListPageInfo?.endCursor });
    const categoryOptions = [
        { label: 'All Categories', value: '' },
        ...(categories || []).map((category) => ({
            label: category.name,
            value: category.slug,
        })),
    ];
    const { width } = useWindowDimensions();

    const [selectedCategory, setSelectedCategory] = useState(null);

    const handleCategorySelectionChange = async (selectedOption) => {
        setSelectedCategory(selectedOption);
        setCursors({ 0: null });
        setBlogList([]);
        fetchBlogs(true, { 1: null }, searchTerm, selectedOption)
    };

    const fetchBlogs = async (reset = false, cursors, searchTerm, selectedCategory) => {
        try {
            setAreBlogListLoading(true);
            if (reset) filterSectionRef?.current?.scrollIntoView();
            const currentPage = Object.keys(cursors).length;
            const blogListResult = await client.query({
                query: FETCH_BLOGS,
                variables: {
                    first: reset ? 25 : itemsPerPage,
                    after: cursors[currentPage-1],
                    searchTerm: searchTerm,
                    category: selectedCategory?.value,
                },
            });

            setBlogList((prevBlogs) => reset ? (blogListResult?.data?.posts?.nodes || []) : [...prevBlogs, ...(blogListResult?.data?.posts?.nodes || [])]);
            setBlogListPageInfo(blogListResult?.data?.posts?.pageInfo || {});
            setCursors((prevCursors) => ({
                ...prevCursors,
                [currentPage]: blogListResult?.data?.posts?.pageInfo?.endCursor,
            }));
        } catch (error) {
            console.error('Error fetching more blogs:', error);
        } finally {
            setTimeout(() => {
                setAreBlogListLoading(false);
            }, 1000);
        }
    }

    const handleSearchBoxText = debounce((searchVal) => {
         // Check if searchVal length is greater than 2
         if (searchVal.length > 2) {
            setSearchTerm(searchVal);
            setCursors({ 0: null });
            setBlogList([]);
            fetchBlogs(true, { 1: null }, searchVal, selectedCategory);
        } else if (searchTerm && searchVal.length === 0) {
            setSearchTerm(searchVal);
            setCursors({ 0: null });
            setBlogList([]);
            fetchBlogs(true, { 1: null }, searchVal, selectedCategory);
        }
    }, 500);

    const resetSearch = () => {
        setLastSearchedTerm('');
        setSearchTerm('');
        setCursors({ 0: null });
        setBlogList([]);
        fetchBlogs(true, { 1: null }, '', selectedCategory);
    };

    const renderBlog = (blog, index, showByDefault = false, loading) => {
        return (
            (featuredBlog?.node.id !== blog.id || showByDefault) && (
                <BlogListItem
                    item={blog}
                    key={blog.id}
                    isLoading={loading}
                    cssClassName={`transition-transform ease-in duration-200 blog-list-item h-full ${!areBlogListLoading && 'w-full flex justify-center'
                        } delay-${(index - 1) * 25}`}
                />
            )
        );
    };

    const observer = useRef();
    const lastBlogElementRef = useCallback(node => {
        if (areBlogListLoading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && blogListPageInfo?.hasNextPage) {
                fetchBlogs(false, cursors, searchTerm, selectedCategory)
            }
        });
        if (node) observer.current.observe(node);
    }, [areBlogListLoading, blogListPageInfo]);

    return (
        <>
            <NextHeadTag {...HeadTagData?.['/blogs']} />
            <div className="px-6 lg:px-12 xl:px-28 py-10 font-paragraph bg-bgColor-50 min-h-screen">
                <Breadcrumbs
                    children={getBreadCrumbs(blogBreadcrumbs())}
                    className="flex pb-1"
                    separator="/"
                />
                <h1 className="text-xl xl:text-2xl font-semibold">Blogs</h1>
                <div className="xl:py-4">
                    <div className="flex flex-col">
                        {featuredBlog ? <div className="order-2 md:order-1 mb-6 md:mb-0">
                            {width < 768 ? (
                                renderBlog(featuredBlog?.node, -1, !searchTerm)
                            ) : (
                                <FeaturedBlog
                                    item={featuredBlog?.node}
                                    isLoading={false}
                                />
                            )}
                        </div> : null}
                        <div className="order-1 md:order-2">
                            <BlogFilters
                                ref={filterSectionRef}
                                categoryOptions={categoryOptions}
                                onCategorySelect={handleCategorySelectionChange}
                                selectedCategory={selectedCategory}
                                onFiltersReset={resetSearch}
                                searchText={lastSearchedTerm}
                                onSearchTextChange={(evt) => {
                                    const searchVal = evt?.target?.value ?? '';
                                    // Allow only alphanumeric characters and spaces
                                    if (regexPattern.ALLOW_BLOGS_SLUG.test(searchVal)) {
                                        setLastSearchedTerm(searchVal);
                                        handleSearchBoxText(searchVal)
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <BlogSearchResult
                        blogs={blogList}
                        areBlogListLoading={areBlogListLoading}
                    />
                    <MasonryGrid>
                        {(blogList).map((blog, index) => {
                            if (blogList.length === index + 1) {
                                return (
                                    <div ref={lastBlogElementRef} key={blog.id}>
                                        {renderBlog(
                                            blog,
                                            index,
                                            width < 768 && searchTerm
                                        )}
                                    </div>
                                );
                            } else {
                                return renderBlog(
                                    blog,
                                    index,
                                    width < 768 && searchTerm
                                );
                            }
                        })}
                        {areBlogListLoading
                            ? dummyBlogs.map((item) =>
                                  renderBlog(item, item.id, false, true)
                              )
                            : null}
                    </MasonryGrid>
                </div>
            </div>
        </>
    );
}

const BlogsWrapper = (props) => {
    useEffect(() => {
        logEvent({eventName: moengageEventConstants.VIEWED_BLOGS_LISTING_PAGE,});
    }, []);
    return (
        <ApolloProvider client={client}>
            <Blogs {...props} />
        </ApolloProvider>
    );
};

export async function getStaticProps(ctx) {
    const categoriesResult = await client.query({
        query: FETCH_ALL_CATEGORIES,
        fetchPolicy: 'no-cache',
    });

    const blogListResult = await client.query({
        query: FETCH_BLOGS,
        variables: baseBlogFilters,
        fetchPolicy: 'no-cache',
    });

    const featuredBlogData = await client.query({
        query: FETCH_FEATURED_BLOG,
        variables: { tag: 'featured' },
        fetchPolicy: 'no-cache',
    });

    return {
        props: {
            categories: categoriesResult?.data?.categories?.nodes || [],
            blogList: blogListResult?.data?.posts?.nodes || [],
            blogListPageInfo: blogListResult?.data?.posts?.pageInfo || {},
            featuredBlog: featuredBlogData?.data?.posts?.edges?.[0] || null,
        },
    };
}

export default BlogsWrapper;